import { useEffect, useState } from 'react'

// The member directory, joined against ids on the client — P6-80.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE ONE PLACE AN ID BECOMES A NAME, FOR EVERY SURFACE
// ═══════════════════════════════════════════════════════════════════════════
//
// Spec §6: *"Site pages fetch the directory through the Function (signed-in,
// like any 3a object), build an id→name map once, and join locally."* This is
// that map, and `nameFor` is the join. Every surface that will ever name
// somebody — T9's compare screen today, the portal home, movie history, the
// music canon's provenance — resolves through here rather than reading
// `members[id].name` itself, for the same reason `compare.js::describePeople`
// exists: a second resolution site is how two screens start disagreeing about
// what somebody is called.
//
// ⚠️ THE NICK→USERNAME CHAIN IS NOT HERE, AND MUST NOT BE. Spec §2: *"the chain
// is resolved by the PUBLISHER, not the client — pages never see the
// nick/username distinction, only `name`."* The blob carries one field and this
// file reads one field. `global_name` is deliberately not a rung of P6-63's
// chain (ADR-0003 A1.4), and the only way to keep that true on every surface is
// for no surface to be able to choose.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT CANNOT THROW, AND IT CANNOT BE AWAITED IN ANYBODY'S CRITICAL PATH
// ═══════════════════════════════════════════════════════════════════════════
//
// Spec §7 item 5: *"Kill R2: pages render with placeholder names and everything
// else intact — names are decoration under the same rule as enrichment, and
// their absence is labeled, not blank."*
//
// `fetchDirectory` has no rejecting branch: offline, 500, 503, a body that is
// not JSON, no Functions at all in `vite dev` — every one of them resolves to
// `unavailable`, which renders placeholders and says so. That is stronger than
// `api.js`'s `BoardError`, and deliberately: the board's failure IS the screen's
// failure, and this one must never be.
//
// ⚠️ WHICH MEANS NO CALLER NEEDS A `catch`, AND NO CALLER MAY USE `Promise.all`
// WITH IT ANYWAY. `loadEnrichment`'s header says the same thing for the same
// reason; the difference is that this one keeps the promise from rejecting so a
// caller cannot get it wrong. See `useDirectory`.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THREE STATES, AND `loading` IS NOT `unavailable`
// ═══════════════════════════════════════════════════════════════════════════
//
// This is `enrichment.js`'s `absentLabel` lesson, one feature over: for the
// first moment of every page load NOTHING HAS FAILED. Saying "names are
// unavailable" there would be the page reporting an outage that has not
// happened. So `loading` says nothing at all and only a directory that actually
// failed gets a sentence.

export const MEMBERS_ENDPOINT = '/api/members'

export const LOADING = 'loading'
export const READY = 'ready'
export const UNAVAILABLE = 'unavailable'

/**
 * What an id we cannot name renders as.
 *
 * 🔴 §6: *"An id absent from the directory renders as a neutral placeholder —
 * never blank, never the raw id."* Both halves matter and the second is the one
 * that would be got wrong: a Discord id is a real identifier that belongs to a
 * real person, printing it says nothing a human can read, and it looks like a
 * bug rather than like an absence.
 *
 * It is neutral about WHY, too. A member who joined since the last reconcile,
 * a member the publisher could not resolve, and a directory that did not load
 * are three different facts and one of them is on the page already (the note
 * below). A per-id explanation would be three sentences beside six avatars.
 */
export const UNKNOWN_NAME = 'someone in the crew'

/** How a departed member's name is qualified. Spec §4: they are rendered, and
 * they are rendered DISTINCTLY — *"a row of raw ids in last year's MOTY is the
 * wrong answer for a group of friends"*, and neither is a name that reads as if
 * they are still here. */
export const LEFT_SUFFIX = 'left the server'

const DAY = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})
const CLOCK = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
})

/**
 * `as_of` as "22 Aug 2026, 02:53 UTC", or null.
 *
 * 🔴 UTC, EXPLICITLY, AND THE TIME IS NOT DROPPED. `dates.js` fixes the zone for
 * a `DATE` column that has none; this fixes it because the stamp is an INSTANT
 * and rendering it in the reader's zone would give six people six different
 * answers to *"how old are these names"*. The clock survives because the
 * publisher re-checks on every reconcile sweep — a date alone would call a
 * directory refreshed an hour ago and one refreshed at midnight the same age.
 */
export function asOfLabel(iso) {
  if (typeof iso !== 'string' || iso === '') return null
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return null
  return `${DAY.format(parsed)}, ${CLOCK.format(parsed)} UTC`
}

/** The directory nobody has answered for yet. Renders placeholders and says
 * nothing, because nothing has failed. */
export function loadingDirectory() {
  return { state: LOADING, asOf: null, byId: new Map(), count: 0 }
}

/** The directory that did not load. Renders placeholders and says so. */
export function unavailableDirectory() {
  return { state: UNAVAILABLE, asOf: null, byId: new Map(), count: 0 }
}

/**
 * One row's avatar, or null. P6-201.
 *
 * 🔴 THE WHOLE OF THE SITE'S KNOWLEDGE ABOUT DISCORD'S CDN IS THAT IT SPEAKS
 * `https:`. `utils/member_directory.py` publishes the finished URL — the guild
 * avatar's path, the account avatar's path, the `a_`-prefix ⇒ `.gif` rule and
 * the size are all resolved there, by discord.py, against an exact pin. Nothing
 * here reconstructs any of it, so nothing here can fall out of step with it.
 *
 * ⚠️ WHY THERE IS A CHECK AT ALL, GIVEN OUR OWN BOT WRITES THE BLOB: this value
 * becomes an `<img src>`. `https:` refuses `data:` and `javascript:` outright
 * and refuses `http:`, which a browser on our HTTPS origin would block as mixed
 * content and render as the broken image P6-201 forbids — an outcome the letter
 * circle handles far better. It deliberately does NOT pin a host: a host
 * allow-list here and a URL builder in the publisher would be two readings of
 * one contract, and the failure mode of disagreeing is every avatar silently
 * vanishing (gotcha 21). The publisher refuses to PUBLISH a non-`https:` URL;
 * this refuses to RENDER one. Same sentence, both ends.
 *
 * A row from a blob written before P6-201 has no such key at all, which lands
 * here as `undefined` and leaves with `null` — the same answer as a member who
 * has no picture, and the same rendering.
 */
function avatarUrl(row) {
  const raw = typeof row?.avatar_url === 'string' ? row.avatar_url.trim() : ''
  return raw.startsWith('https://') ? raw : null
}

/**
 * The Function's answer as an id→name map.
 *
 * 🔴 A ROW WITH NO USABLE NAME IS DROPPED RATHER THAN KEPT, and that is §6's
 * *"never blank"* enforced where it can actually be enforced. The publisher
 * already refuses to store an empty name (`MemberDirectoryStore.record` raises),
 * so this is the second half of the same rule rather than a duplicate of it:
 * whatever reaches the browser, a name that would render as nothing becomes an
 * ABSENT id, which renders as the placeholder. A blank chip is the one outcome
 * neither end may produce.
 *
 * Ids are coerced to strings because they arrive as JSON object keys (always
 * strings) and are compared against ids from the trip service (also strings) —
 * stated because a number here would silently miss every lookup.
 */
export function buildDirectory(payload) {
  const members = payload?.members
  if (!members || typeof members !== 'object' || Array.isArray(members)) {
    return unavailableDirectory()
  }
  const byId = new Map()
  for (const [id, row] of Object.entries(members)) {
    const name = typeof row?.name === 'string' ? row.name.trim() : ''
    if (name === '') continue
    byId.set(String(id), { name, left: row?.left === true, avatarUrl: avatarUrl(row) })
  }
  return {
    state: READY,
    asOf: typeof payload?.as_of === 'string' ? payload.as_of : null,
    byId,
    count: byId.size,
  }
}

/**
 * One id, resolved. Never throws, never returns a blank, never returns the id.
 *
 * `known` is separate from `label` on purpose: a caller that wants to say
 * something different about a person we cannot name (the compare screen's *"only
 * one of you"*) needs the fact, not a string it has to compare against the
 * placeholder to recover it.
 *
 * ⚠️ `avatarUrl` IS NULL FAR MORE OFTEN THAN IT IS A STRING, and every caller
 * has to have a real answer for that rather than a spinner or a gap: an id the
 * directory has never seen, a member who has set no picture, a member who has
 * LEFT (the publisher drops a departed member's avatar — a face is the
 * strongest *"still here"* signal a page has), and a directory that did not
 * load at all. All four are one state here on purpose, for the reason
 * `UNKNOWN_NAME` is neutral about why: they are four different facts and a
 * per-id explanation would be four sentences beside six circles.
 */
export function nameFor(directory, userId) {
  const row = typeof userId === 'string' ? directory?.byId?.get(userId) : undefined
  if (!row) return { label: UNKNOWN_NAME, left: false, known: false, avatarUrl: null }
  return {
    label: row.name,
    left: row.left === true,
    known: true,
    avatarUrl: row.avatarUrl ?? null,
  }
}

/**
 * The sentence a surface prints beside people, or null.
 *
 * 🔴 SPEC §2: *"`as_of` is displayed wherever staleness could mislead."* A name
 * is exactly the kind of fact that goes stale silently — somebody changes their
 * nickname and every screen keeps saying the old one until the next reconcile,
 * with nothing on the page admitting it. So a surface that names people prints
 * where the names came from and when they were last checked.
 *
 * And the absence is labeled rather than blank, per §7 item 5. Three sentences,
 * one per state, and `loading` gets none — see the header.
 */
export function directoryNote(directory) {
  if (directory?.state === UNAVAILABLE) {
    return 'Names are unavailable right now, so people show as colours. Nothing else on this page is affected.'
  }
  if (directory?.state !== READY) return null
  const when = asOfLabel(directory.asOf)
  if (when === null) {
    return 'The directory does not say when these names were last checked.'
  }
  return `Names as of ${when}.`
}

/**
 * Ask the Function. Resolves to a directory, always — see the header.
 *
 * ⚠️ `credentials: 'same-origin'`: the session cookie IS the caller's identity
 * and a fetch that dropped it would be answered as signed out, which this would
 * then report as "unavailable" — names quietly missing for everybody, from one
 * absent option.
 */
export async function fetchDirectory(fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return unavailableDirectory()
  try {
    const response = await fetchImpl(MEMBERS_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) return unavailableDirectory()
    return buildDirectory(await response.json())
  } catch {
    return unavailableDirectory()
  }
}

/**
 * The directory for one screen. Starts `loading`, lands `ready` or `unavailable`.
 *
 * ⚠️ ONE REQUEST PER SCREEN THAT WANTS IT, AND NO MODULE-LEVEL CACHE — the same
 * trade `session.js` made and for the reason it wrote down: a promise cache
 * saves a request and buys shared mutable state that every test has to reset.
 * The answer is small, `no-store`, and asked for once per mount. If a third
 * surface ever wants it on the same page, reconsider then and not before.
 */
export function useDirectory() {
  const [directory, setDirectory] = useState(loadingDirectory)

  useEffect(() => {
    let cancelled = false
    // No `.catch`: `fetchDirectory` has no rejecting branch, and a catch here
    // would be a handler that can never run — which reads as if it could.
    fetchDirectory().then((answer) => {
      if (!cancelled) setDirectory(answer)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return directory
}
